import { useCallback, useEffect, useRef, useState } from 'react'

const generations = new Map()
let nextGeneration = 1

function diagnostic(event, entry, fields = {}) {
  console.info('[members-identity]', {
    event,
    clubCode: entry.clubCode,
    generation: entry.generation,
    requestId: entry.requestId,
    ...fields,
  })
}

export function createCanonicalIdentityKey(clubCode, subject, organization) {
  return [clubCode || '', subject || '', organization || ''].map(encodeURIComponent).join('::')
}

function newEntry(key, clubCode) {
  return {
    key,
    clubCode,
    generation: nextGeneration++,
    requestId: globalThis.crypto?.randomUUID?.() || `identity-${Date.now()}-${nextGeneration}`,
    status: 'idle',
    data: null,
    error: null,
    promise: null,
    listeners: new Set(),
  }
}

function notify(entry) { entry.listeners.forEach((listener) => listener(entry)) }

function start(entry, fetchIdentity) {
  if (entry.promise) {
    diagnostic('canonical_identity_request_joined', entry, { reason: 'inflight' })
    return entry.promise
  }
  if (entry.status === 'succeeded') return Promise.resolve(entry.data)
  entry.status = 'loading'
  const began = performance.now()
  diagnostic('canonical_identity_generation_started', entry, { reason: 'identity_ready' })
  notify(entry)
  const promise = Promise.resolve().then(fetchIdentity).then(async (response) => {
    const body = await response.json().catch(() => null)
    if (!response.ok || body?.ok !== true) throw Object.assign(new Error('invalid_identity_response'), { status: response.status })
    // The entry is identity-bound. A stale completion only updates its old entry;
    // subscribers for a newer key can never observe it.
    entry.data = body.data || null
    entry.status = 'succeeded'
    entry.error = null
    diagnostic('canonical_identity_succeeded', entry, { durationMs: Math.round(performance.now() - began) })
    notify(entry)
    return entry.data
  }).catch((error) => {
    entry.status = 'failed'
    entry.error = error
    notify(entry)
    throw error
  }).finally(() => { entry.promise = null })
  entry.promise = promise
  return promise
}

/** Process-wide identity projection: one settled generation for each exact auth identity. */
export function useCanonicalIdentityResource({ clubCode, subject, organization, enabled, fetchIdentity }) {
  const key = createCanonicalIdentityKey(clubCode, subject, organization)
  const fetchRef = useRef(fetchIdentity)
  fetchRef.current = fetchIdentity
  const [, render] = useState(0)
  let entry = generations.get(key)
  if (!entry) {
    entry = newEntry(key, clubCode)
    generations.set(key, entry)
  }
  const entryRef = useRef(entry)
  const previousEntryRef = useRef(entry)
  entryRef.current = entry

  useEffect(() => {
    if (previousEntryRef.current.key !== entry.key) {
      diagnostic('canonical_identity_generation_invalidated', previousEntryRef.current, { reason: 'identity_changed' })
      previousEntryRef.current = entry
    }
    const listener = () => render((value) => value + 1)
    entry.listeners.add(listener)
    if (enabled) start(entry, () => fetchRef.current()).catch(() => {})
    return () => entry.listeners.delete(listener)
  }, [enabled, entry, key])

  const retry = useCallback(() => {
    const previous = generations.get(key)
    const replacement = newEntry(key, clubCode)
    generations.set(key, replacement)
    diagnostic('canonical_identity_generation_invalidated', previous || replacement, { reason: 'explicit_retry' })
    entryRef.current = replacement
    render((value) => value + 1)
    return start(replacement, () => fetchRef.current())
  }, [clubCode, key])

  return {
    identityKey: key,
    data: entryRef.current.key === key ? entryRef.current.data : null,
    loading: enabled && ['idle', 'loading'].includes(entryRef.current.status),
    error: entryRef.current.error,
    retry,
  }
}

export function clearCanonicalIdentityResourcesForTests() {
  generations.clear()
  nextGeneration = 1
}


/** Invalidate live identity generations so late responses cannot become reusable state. */
export function invalidateCanonicalIdentityResources() {
  generations.forEach((entry) => {
    entry.listeners.clear()
    entry.status = 'invalidated'
    entry.data = null
  })
  generations.clear()
  nextGeneration += 1
}
